// Care Transitions HTTP — the generated `CcdaReceiveClient` (the 791 endpoints landed in
// @medone/medonehp-api-client on 2026-07-09, retiring the hand-rolled Axios layer). We pass
// getEnvBaseUrl() so the client emits ABSOLUTE URLs (the shared Axios request interceptor rejects
// relative ones) and the shared `Axios` instance for auth + URL rewriting.
//
// The generated DTOs (all-optional fields, moment.Moment dates) are mapped here to the plain,
// string-dated view models the Redux store holds. This mapping is the anti-corruption boundary:
// a future client change that drops/renames a field fails to compile HERE, not silently in the UI.
import moment, { Moment } from 'moment';

import {
    CcdaDocumentClient,
    CcdaIngestRequest,
    CcdaReceiptConfirmationRequest,
    CcdaReceiptReasons,
    CcdaReceiveClient,
    CcdaResolutionOutcomeDto,
    CcdaUnmatchedReasons,
    Genders,
    ExistingItemDto as GenExisting,
    ReconcileItemDto as GenItem,
    ReconcileListDto as GenList,
    CcdaReconcileSessionDto as GenSession,
    ReconcileListSummaryDto as GenSummary,
    MarkNewPatientRequest,
    MarkUnmatchedRequest,
    MatchStatus,
    ReconcileDecision,
    ReconcileDecisionRequest,
    ReconcileListType,
    ResolvePatientRequest,
    SexAtBirths,
} from '@medone/medonehp-api-client';

import { handleError } from '../../../../shared/common/HandleErrors';
import { getEnvBaseUrl } from '../../../../shared/common/helpers';
import { genderLabel } from '../../../../shared/common/helpers/gender-labels';
import { Axios } from '../../../../shared/common/http';
import { appInsights } from '../../../../shared/common/telemetry';
import {
    CcdaFetchMessageDto,
    CcdaFetchOverrides,
    CcdaFetchRunDetailDto,
    CcdaFetchRunDto,
    CcdaFetchRunFilter,
    CcdaInboxFilter,
    CcdaInboxPagination,
    CcdaIngestResultView,
    CcdaMatchCriteriaView,
    CcdaMatchTier,
    CcdaPatientResolutionView,
    CcdaReceiptReason,
    CcdaReceiveAuditEntryDto,
    CcdaReceiveListItemDto,
    CcdaReceiveStatus,
    CcdaReceiveValidationReportDto,
    CcdaReconcileSessionDto,
    CcdaReconcileSessionListDto,
    CcdaResolutionCandidateView,
    CcdaResolutionDocumentView,
    CcdaResolutionOutcome,
    CcdaTransmissionAuditEntryDto,
    CcdaTransmissionAuditFilter,
    CcdaTransmitResultDto,
    CcdaTransmitSmtpOverride,
    CcdaUnmatchedReason,
    PatchDecisionBody,
    ReconcileExistingItemDto,
    ReconcileIncomingItemDto,
    ReconcileListDto,
} from './models';

// A fresh client per call so getEnvBaseUrl() is resolved at request time (matches the old per-call URL build).
const client = () => new CcdaReceiveClient(getEnvBaseUrl(), Axios);

// Error carrying the HTTP status so callers can branch (e.g. 409 optimistic-concurrency), plus the stable
// `errorCode` a ProblemDetails may carry — a status alone cannot separate two faults that share one status.
interface ApiError extends Error {
    status?: number;
    errorCode?: string;
    /** The attempt row a recorded transport failure wrote, when the server names it (SCRUM-1353). */
    ccdaTransmissionId?: number;
    /** The reference sent to Zen with a Zen/SES attempt, identifying it beside `ccdaTransmissionId`. */
    clientReference?: string;
    /**
     * A ProblemDetails `detail`, and ONLY from a 4xx. The 4xx mappings in StartupExtensions build `detail`
     * from a message we authored at the throw site, so it is caller-facing by construction. A 5xx `detail` is
     * not: the unhandled-exception hook overwrites it with the raw `ex.Message` of whatever threw, which is
     * how MailKit and driver prose would reach the UI. Read for 4xx, dropped for everything else.
     */
    detail?: string;
}

// NSwag throws an `ApiException` for a real HTTP error (status + raw response body + a GENERIC message),
// so the backend's actionable text (v4 real statuses) lives in the parsed body, not `.message`. Normalize
// every failure into an Error carrying that message + status — never surface "An unexpected server error…".
const isGenericMessage = (m: string) => /status code \d+|unexpected server error|server side error occurred/i.test(m);

const toApiError = (err: unknown, fallback: string): ApiError => {
    // NSwag ApiException carries `.status` at the top level; a raw AxiosError (the hand-rolled `commit`) carries it on `.response`.
    let status = typeof (err as { status?: unknown })?.status === 'number' ? (err as { status: number }).status : undefined;
    let message: string | undefined;
    // ProblemDetails serialises its extension members as top-level keys, so a typed backend code arrives beside
    // `title`/`detail` rather than inside them.
    let errorCode: string | undefined;
    let detail: string | undefined;
    let ccdaTransmissionId: number | undefined;
    let clientReference: string | undefined;
    const readErrorCode = (body: unknown) => {
        const code = (body as { errorCode?: unknown } | null)?.errorCode;
        if (typeof code === 'string' && code) errorCode = code;

        // Identifiers only, from the same extension members as the code — never prose.
        const transmissionId = (body as { ccdaTransmissionId?: unknown } | null)?.ccdaTransmissionId;
        if (typeof transmissionId === 'number') ccdaTransmissionId = transmissionId;
        const reference = (body as { clientReference?: unknown } | null)?.clientReference;
        if (typeof reference === 'string' && reference) clientReference = reference;

        // 4xx only — see the note on ApiError.detail. Kept off `message` deliberately: promoting it there
        // would change what every existing caller renders, and a caller that wants it can ask for it.
        const problemDetail = (body as { detail?: unknown } | null)?.detail;
        if (typeof problemDetail === 'string' && problemDetail && status != null && status >= 400 && status < 500) {
            detail = problemDetail;
        }
    };
    const response = (err as { response?: unknown })?.response;
    if (typeof response === 'string' && response) {
        // NSwag ApiException.response = raw body string.
        try {
            const parsed = JSON.parse(response);
            if (typeof parsed?.message === 'string' && parsed.message) message = parsed.message;
            readErrorCode(parsed);
        } catch {
            /* body wasn't JSON */
        }
    } else if (response && typeof response === 'object') {
        // TWO shapes land here. A raw AxiosError carries `{ status, data }`, but NSwag's ApiException carries the
        // body axios ALREADY deserialised — so for a JSON ProblemDetails `response` IS the body, with no `data`
        // wrapper. Reading only `.data` silently loses everything the backend sent on that path.
        const r = response as { status?: number; data?: unknown };
        if (status == null && typeof r.status === 'number') status = r.status;
        const body = r.data !== undefined ? r.data : response;

        // A STRING body is one axios could not parse as JSON: an ASP.NET developer-exception page — which
        // answers in text/plain whenever the caller does not accept text/html, i.e. every request from here —
        // a proxy's plain-text 5xx, or a bare exception message. It is read for the typed code and then
        // DISCARDED rather than promoted to `message`. Promoting it is how a full .NET stack trace reached the
        // Send modal's Alert, and it contradicts the rule this file already states for `fullException`: a raw
        // server stack trace must not reach the UI. The caller's fallback is the operator-facing text.
        //
        // Parsed rather than ignored outright because one caller genuinely arrives here with unparsed JSON:
        // fetchReceivedDocumentXml sets responseType 'text', which disables axios's JSON parsing.
        let parsed: unknown = body;
        if (typeof body === 'string') {
            try {
                parsed = JSON.parse(body);
            } catch {
                parsed = null;
            }
        }
        if (parsed && typeof parsed === 'object' && typeof (parsed as { message?: unknown }).message === 'string' && (parsed as { message: string }).message) {
            message = (parsed as { message: string }).message;
        }
        readErrorCode(parsed);
    }
    if (!message) {
        const result = (err as { result?: { message?: unknown } })?.result;
        if (typeof result?.message === 'string' && result.message) message = result.message;
    }
    // An Error we threw from `check`/entity-guard already holds the backend (or fallback) message.
    if (!message && err instanceof Error && err.message && !isGenericMessage(err.message)) {
        message = err.message;
    }
    const apiError = new Error(message ?? fallback) as ApiError;
    apiError.status = status;
    apiError.errorCode = errorCode;
    apiError.detail = detail;
    apiError.ccdaTransmissionId = ccdaTransmissionId;
    apiError.clientReference = clientReference;
    return apiError;
};

// Envelope guard — surfaces a toast on a 200-with-succeeded:false (repo pattern) and throws the message.
const check = (response: { result: { succeeded?: boolean; message?: string; fullException?: string } }, fallback: string): void => {
    const r = response.result;
    if (!r.succeeded) {
        handleError(response as any, () => {});
        throw new Error(r.message || r.fullException || fallback);
    }
};

// The ApiResponse<T> envelope as it arrives on the wire (top-level; the generated client nests it under `.result`).
interface ApiEnvelope<T> {
    succeeded?: boolean;
    message?: string;
    fullException?: string;
    entity?: T;
}

// Envelope guard for a hand-rolled Axios call that sees the ApiResponse body directly. Toasts + throws like
// `check`, but never surfaces `fullException` — a raw server stack trace must not reach the UI (info disclosure).
const checkEnvelope = (env: ApiEnvelope<unknown>, fallback: string): void => {
    if (!env.succeeded) {
        handleError({ result: env } as any, () => {});
        throw new Error(env.message || fallback);
    }
};

// --- generated → view-model mappers (moment.Moment → ISO string; optional → required with safe defaults) ---
const iso = (m: Moment | null | undefined): string | null => (m ? m.toISOString() : null);

// The queue + audit endpoints carry new sender fields the published NSwag client doesn't expose yet, so both are
// hand-rolled against the shared Axios (see listReceived/fetchReceiveAudit) and mapped from the raw JSON here.
// These revert to the generated client + moment mappers once it is regenerated (mirrors the `commit` precedent).
interface RawReceived {
    stagingId?: number;
    status?: string;
    receivedDate?: string | null;
    documentType?: string;
    matchesFound?: number;
    suggestedPatientId?: number | null;
    resolvedPatientId?: number | null;
    sourceFirstName?: string | null;
    sourceLastName?: string | null;
    sourceDateOfBirth?: string | null;
    sourceGender?: number | null;
    senderName?: string | null;
    senderDirectAddress?: string | null;
    senderTransport?: string | null;
    resolvedPatientName?: string | null;
    facilityName?: string | null;
    providerName?: string | null;
    failureReason?: string | null;
    ccdaVersion?: string | null;
    hasDocument?: boolean;
    errorCount?: number;
    warningCount?: number;
    matchedByName?: string | null;
    matchedDate?: string | null;
    providerTin?: string | null;
}

const mapReceived = (d: RawReceived): CcdaReceiveListItemDto => ({
    stagingId: d.stagingId ?? 0,
    status: (d.status ?? 'PendingMatch') as CcdaReceiveStatus,
    receivedDate: d.receivedDate ?? null,
    documentType: d.documentType ?? '',
    matchesFound: d.matchesFound ?? 0,
    suggestedPatientId: d.suggestedPatientId ?? null,
    resolvedPatientId: d.resolvedPatientId ?? null,
    sourceFirstName: d.sourceFirstName ?? null,
    sourceLastName: d.sourceLastName ?? null,
    sourceDateOfBirth: d.sourceDateOfBirth ?? null,
    sourceGender: d.sourceGender ?? null,
    senderName: d.senderName ?? null,
    senderDirectAddress: d.senderDirectAddress ?? null,
    transport: d.senderTransport ?? null,
    resolvedPatientName: d.resolvedPatientName ?? null,
    facilityName: d.facilityName ?? null,
    providerName: d.providerName ?? null,
    failureReason: d.failureReason ?? null,
    ccdaVersion: d.ccdaVersion ?? null,
    hasDocument: d.hasDocument ?? false,
    errorCount: d.errorCount ?? 0,
    warningCount: d.warningCount ?? 0,
    matchedByName: d.matchedByName ?? null,
    matchedDate: d.matchedDate ?? null,
    providerTin: d.providerTin ?? null,
});

interface RawAuditEntry {
    stagingId?: number;
    eventType?: string;
    timestamp?: string | null;
    userId?: string;
    userName?: string | null;
    documentType?: string | null;
    senderName?: string | null;
    senderDirectAddress?: string | null;
    senderTransport?: string | null;
    failureReason?: string | null;
}

const mapAuditEntry = (d: RawAuditEntry): CcdaReceiveAuditEntryDto => ({
    stagingId: d.stagingId ?? 0,
    eventType: d.eventType ?? '',
    timestamp: d.timestamp ?? null,
    userId: d.userId ?? '',
    userName: d.userName ?? null,
    documentType: d.documentType ?? null,
    senderName: d.senderName ?? null,
    senderDirectAddress: d.senderDirectAddress ?? null,
    senderTransport: d.senderTransport ?? null,
    failureReason: d.failureReason ?? null,
});

const mapSummary = (d: GenSummary): CcdaReconcileSessionListDto => ({
    listType: d.listType ?? ReconcileListType.Problem,
    total: d.total ?? 0,
    pending: d.pending ?? 0,
    accepted: d.accepted ?? 0,
    rejected: d.rejected ?? 0,
    reviewed: d.reviewed ?? false,
});

const mapSession = (d: GenSession): CcdaReconcileSessionDto => ({
    stagingId: d.stagingId ?? 0,
    status: (d.status ?? 'PendingMatch') as CcdaReceiveStatus,
    receivedDate: iso(d.receivedDate),
    documentType: d.documentType ?? '',
    matchesFound: d.matchesFound ?? 0,
    suggestedPatientId: d.suggestedPatientId ?? null,
    resolvedPatientId: d.resolvedPatientId ?? null,
    documentSourceLabel: d.documentSourceLabel ?? null,
    lists: (d.lists ?? []).map(mapSummary),
});

const mapIncoming = (d: GenItem): ReconcileIncomingItemDto => ({
    id: d.id ?? 0,
    listType: d.listType ?? ReconcileListType.Problem,
    code: d.code ?? null,
    codeSystemOid: d.codeSystemOid ?? null,
    displayName: d.displayName ?? null,
    onsetDate: iso(d.onsetDate),
    statusText: d.statusText ?? null,
    severity: d.severity ?? null,
    sourceLabel: d.sourceLabel ?? null,
    sourceLastModified: iso(d.sourceLastModified),
    decision: d.decision ?? ReconcileDecision.Pending,
    consolidatedIntoItemId: d.consolidatedIntoItemId ?? null,
    matchedExistingKey: d.matchedExistingKey ?? null,
    matchStatus: d.matchStatus ?? MatchStatus.New,
    conflictFields: d.conflictFields ?? [],
    rowVersion: d.rowVersion ?? '',
    sourceAttributesJson: d.sourceAttributesJson ?? null,
});

const mapExisting = (d: GenExisting): ReconcileExistingItemDto => ({
    key: d.key ?? '',
    code: d.code ?? null,
    codeSystemOid: d.codeSystemOid ?? null,
    displayName: d.displayName ?? null,
    onsetDate: iso(d.onsetDate),
    statusText: d.statusText ?? null,
    severity: d.severity ?? null,
    sourceLabel: d.sourceLabel ?? null,
    lastModified: iso(d.lastModified),
});

const mapList = (d: GenList): ReconcileListDto => ({
    listType: d.listType ?? ReconcileListType.Problem,
    reviewed: d.reviewed ?? false,
    incoming: (d.incoming ?? []).map(mapIncoming),
    existing: (d.existing ?? []).map(mapExisting),
});

// Hand-rolled (not the generated client) so the new sender columns survive; `patientId` omitted = the global
// inbound queue including unmatched documents (SCRUM-921 AC2).
export const listReceived = async (patientId?: number, status?: CcdaReceiveStatus): Promise<CcdaReceiveListItemDto[]> => {
    try {
        const params = new URLSearchParams();
        if (patientId != null) params.set('patientId', String(patientId));
        if (status) params.set('status', status);
        const query = params.toString();
        const response = await Axios.get<ApiEnvelope<RawReceived[]>>(`${getEnvBaseUrl()}/api/ccda-receive${query ? `?${query}` : ''}`);
        checkEnvelope(response.data, 'Failed to load received documents.');
        return (response.data.entity ?? []).map(mapReceived);
    } catch (err) {
        throw toApiError(err, 'Failed to load received documents.');
    }
};

// The AntdTableResponse envelope: the ApiResponse body plus a top-level pagination block.
interface AntdEnvelope<T> extends ApiEnvelope<T> {
    pagination?: { currentPage?: number; pageSize?: number; total?: number; searchTerm?: string | null };
}

export interface CcdaInboxPageResult {
    items: CcdaReceiveListItemDto[];
    pagination: CcdaInboxPagination;
}

// Global C-CDA Exchange admin queue (Admin ▸ System): server-side paged + filtered across all patients, incl.
// unmatched documents. Hand-rolled against the shared Axios (not in the published NSwag client yet, like
// listReceived); POSTs the filter + antd paging and maps the enriched rows + pagination back.
export const fetchInboxPage = async (filter: CcdaInboxFilter, page: { current: number; pageSize: number }): Promise<CcdaInboxPageResult> => {
    try {
        const body = {
            antdTableRequest: { current: page.current, pageSize: page.pageSize },
            patientName: filter.patientName ?? null,
            facilityId: filter.facilityId ?? null,
            providerId: filter.providerId ?? null,
            dateOfBirth: filter.dateOfBirth ?? null,
        };
        const response = await Axios.post<AntdEnvelope<RawReceived[]>>(`${getEnvBaseUrl()}/api/ccda-receive/inbox`, body);
        checkEnvelope(response.data, 'Failed to load the inbound queue.');
        const pg = response.data.pagination;
        return {
            items: (response.data.entity ?? []).map(mapReceived),
            pagination: {
                current: pg?.currentPage ?? page.current,
                pageSize: pg?.pageSize ?? page.pageSize,
                total: pg?.total ?? 0,
            },
        };
    } catch (err) {
        throw toApiError(err, 'Failed to load the inbound queue.');
    }
};

// The original received C-CDA XML for the viewer (Screen 6). Returns raw XML text (not an ApiResponse envelope);
// the viewing itself is audited server-side (CcdaReceivedDocumentViewed).
export const fetchReceivedDocumentXml = async (stagingId: number): Promise<string> => {
    try {
        const response = await Axios.get<string>(`${getEnvBaseUrl()}/api/ccda-receive/${stagingId}/document`, { responseType: 'text' });
        const raw = typeof response.data === 'string' ? response.data : String(response.data);
        // The endpoint returns raw XML on success but a JSON ApiResponse envelope on failure — both at HTTP 200.
        // A C-CDA body always starts with '<'; only a '{' can be an error envelope. Guard so a backend failure
        // surfaces its message instead of being mis-rendered downstream as "The document is not valid XML".
        if (raw.trimStart().startsWith('{')) {
            let env: ApiEnvelope<unknown> | null = null;
            try {
                env = JSON.parse(raw) as ApiEnvelope<unknown>;
            } catch {
                env = null;
            }
            if (env && env.succeeded === false) {
                // Telemetry only (no toast): the viewer already renders the thrown message in its inline Alert, so
                // a handleError toast here would double-report one failure. Never surface `fullException` — a raw
                // server stack trace must not reach the UI (info disclosure); the backend logs keep it.
                appInsights.trackException({ exception: new Error('Failed to load a received C-CDA document.') });
                throw new Error(env.message || 'Failed to load the received document.');
            }
        }
        return raw;
    } catch (err) {
        throw toApiError(err, 'Failed to load the received document.');
    }
};

// SCRUM-1298 — the per-document validation report: the findings the receive gate stored on the staging row, read
// back as recorded. Hand-rolled like the list/document calls (the published NSwag client predates the route).
interface RawValidationFinding {
    code?: string;
    severity?: string;
    category?: string;
    message?: string;
    elementPath?: string | null;
    ruleId?: string | null;
    expected?: string | null;
}

interface RawValidationReport {
    stagingId?: number;
    status?: string;
    ccdaVersion?: string | null;
    failureReason?: string | null;
    errorCount?: number;
    warningCount?: number;
    findings?: RawValidationFinding[] | null;
}

export const fetchValidationReport = async (stagingId: number): Promise<CcdaReceiveValidationReportDto> => {
    try {
        const response = await Axios.get<ApiEnvelope<RawValidationReport>>(`${getEnvBaseUrl()}/api/ccda-receive/${stagingId}/validation-report`);
        checkEnvelope(response.data, 'Failed to load the validation report.');
        const r = response.data.entity ?? {};
        return {
            stagingId: r.stagingId ?? stagingId,
            status: (r.status ?? 'PendingMatch') as CcdaReceiveStatus,
            ccdaVersion: r.ccdaVersion ?? null,
            failureReason: r.failureReason ?? null,
            errorCount: r.errorCount ?? 0,
            warningCount: r.warningCount ?? 0,
            findings: (r.findings ?? []).map((f) => ({
                code: f.code ?? '',
                severity: f.severity ?? 'Error',
                category: f.category ?? 'Structure',
                message: f.message ?? '',
                elementPath: f.elementPath ?? null,
                ruleId: f.ruleId ?? null,
                expected: f.expected ?? null,
            })),
        };
    } catch (err) {
        throw toApiError(err, 'Failed to load the validation report.');
    }
};

// Per-patient received-document audit (SCRUM-921 AC6 / §170.315(d)(2)/(d)(3)); the org-wide report is fetchReceiveAuditPage.
export const fetchReceiveAudit = async (patientId: number): Promise<CcdaReceiveAuditEntryDto[]> => {
    try {
        const response = await Axios.get<ApiEnvelope<RawAuditEntry[]>>(`${getEnvBaseUrl()}/api/ccda-receive/audit?patientId=${patientId}`);
        checkEnvelope(response.data, 'Failed to load the audit log.');
        return (response.data.entity ?? []).map(mapAuditEntry);
    } catch (err) {
        throw toApiError(err, 'Failed to load the audit log.');
    }
};

export interface CcdaAuditPageResult {
    items: CcdaReceiveAuditEntryDto[];
    pagination: CcdaInboxPagination;
}

// Org-wide received-document audit report (Admin ▸ System, §170.315(d)(2)/(d)(3)): admin-only + server-paged.
// Hand-rolled against the shared Axios (not in the published NSwag client yet), mirroring fetchInboxPage.
export const fetchReceiveAuditPage = async (page: { current: number; pageSize: number }): Promise<CcdaAuditPageResult> => {
    try {
        const body = { antdTableRequest: { current: page.current, pageSize: page.pageSize } };
        const response = await Axios.post<AntdEnvelope<RawAuditEntry[]>>(`${getEnvBaseUrl()}/api/ccda-receive/audit/search`, body);
        checkEnvelope(response.data, 'Failed to load the audit log.');
        const pg = response.data.pagination;
        return {
            items: (response.data.entity ?? []).map(mapAuditEntry),
            pagination: {
                current: pg?.currentPage ?? page.current,
                pageSize: pg?.pageSize ?? page.pageSize,
                total: pg?.total ?? 0,
            },
        };
    } catch (err) {
        throw toApiError(err, 'Failed to load the audit log.');
    }
};

export const getSession = async (stagingId: number): Promise<CcdaReconcileSessionDto> => {
    try {
        const response = await client().getSession(stagingId);
        check(response, 'Failed to load reconciliation session.');
        if (response.result.entity == null) throw new Error('Failed to load reconciliation session.');
        return mapSession(response.result.entity);
    } catch (err) {
        throw toApiError(err, 'Failed to load reconciliation session.');
    }
};

export const getList = async (stagingId: number, listType: ReconcileListType): Promise<ReconcileListDto> => {
    try {
        const response = await client().getReconciliationList(stagingId, listType);
        check(response, 'Failed to load reconciliation list.');
        if (response.result.entity == null) throw new Error('Failed to load reconciliation list.');
        return mapList(response.result.entity);
    } catch (err) {
        throw toApiError(err, 'Failed to load reconciliation list.');
    }
};

export const patchDecision = async (stagingId: number, itemId: number, body: PatchDecisionBody): Promise<void> => {
    try {
        const request = new ReconcileDecisionRequest({
            decision: body.decision,
            consolidatedIntoItemId: body.consolidatedIntoItemId ?? null,
            matchedExistingKey: body.matchedExistingKey ?? null,
            rowVersion: body.rowVersion ?? null,
        });
        const response = await client().setDecision(stagingId, itemId, request);
        check(response, 'Failed to save decision.');
    } catch (err) {
        throw toApiError(err, 'Failed to save decision.');
    }
};

export const confirmList = async (stagingId: number, listType: ReconcileListType): Promise<void> => {
    try {
        const response = await client().confirmList(stagingId, listType);
        check(response, 'Failed to confirm list.');
    } catch (err) {
        throw toApiError(err, 'Failed to confirm list.');
    }
};

// Document-level coded incorporation (SCRUM-792). The `commit` endpoint isn't in the published client yet
// (branch unmerged), so this one call is hand-rolled against the shared Axios instance; the thunk re-reads
// the session (via the generated getSession) afterwards, so we only need to surface success/failure here.
export const commit = async (stagingId: number, tin?: string): Promise<void> => {
    try {
        const response = await Axios.post<{ succeeded?: boolean; message?: string; fullException?: string }>(
            `${getEnvBaseUrl()}/api/ccda-receive/${stagingId}/reconciliation/commit`,
            tin ? { tin } : undefined
        );
        const env = response.data;
        if (!env.succeeded) {
            handleError({ result: env } as any, () => {});
            throw new Error(env.message || env.fullException || 'Failed to commit reconciliation.');
        }
    } catch (err) {
        throw toApiError(err, 'Failed to commit reconciliation.');
    }
};

// The TINs the signed-in clinician bills under. Empty while ShowMipsPi is off (the API answers 404) and
// for anyone who is not an eligible clinician, which is what keeps the pickers hidden for them.
export const listMyTins = async (): Promise<string[]> => {
    try {
        const response = await new CcdaDocumentClient(null as any, Axios).getMyTins();

        return response?.result?.entity ?? [];
    } catch {
        return [];
    }
};

// (g)(2) RT7 second numerator criterion (SCRUM-1344): the clinician records that the recipient confirmed
// receipt. CMS accepts a HISP delivery log or a reply from the receiving provider as that confirmation, and
// neither reaches the EHR on its own — the MDN path writes the same outcome automatically when one arrives.
// Behind ShowMipsPi: the API answers 404 while the flag is off.
export const setReceiptConfirmed = async (ccdaDocumentId: number, confirmed: boolean): Promise<Date | null> => {
    try {
        const response = await new CcdaDocumentClient(null as any, Axios).setReceiptConfirmed(
            ccdaDocumentId,
            new CcdaReceiptConfirmationRequest({ confirmed })
        );
        checkEnvelope(response.result, 'Failed to record the receipt confirmation.');

        const at = response.result.entity?.receiptConfirmedAt;

        return at ? at.toDate() : null;
    } catch (err) {
        throw toApiError(err, 'Failed to record the receipt confirmation.');
    }
};

// --- SCRUM-988 Patient Resolution (manual match) ---------------------------------------------------------------
// The three writes use the generated CcdaReceiveClient. The read is hand-rolled against the shared Axios, like
// listReceived: the published client's DTO init() copies only the fields it declares, so it would silently drop
// the SCRUM-1477 matchTier and per-candidate matchedCriteria. Its raw JSON is mapped onto the plain string view
// models. gender/sexAtBirth travel as numeric enums (Genders / SexAtBirths); sexAtBirth is mapped to a display
// label HERE, gender through the shared helpers/gender-labels map so every screen shows the same string
// (SCRUM-1303). checkEnvelope guards the 200-with-succeeded:false envelope so a raw server stack trace
// (fullException) never reaches the UI. The PHI-heavy payload is never cached in Redux (component-local state,
// re-read on reload — AC-5).

// Wire enum → display label. An out-of-range value → null (render "—", not a number).
const SEX_AT_BIRTH_LABELS: Record<SexAtBirths, string> = {
    [SexAtBirths.Male]: 'Male',
    [SexAtBirths.Female]: 'Female',
    [SexAtBirths.Unknown]: 'Unknown',
};
const sexAtBirthLabel = (s: SexAtBirths | null | undefined): string | null => (s == null ? null : (SEX_AT_BIRTH_LABELS[s] ?? null));

const RECEIPT_REASON_TO_ENUM: Record<CcdaReceiptReason, CcdaReceiptReasons> = {
    TransitionOfCare: CcdaReceiptReasons.TransitionOfCare,
    Referral: CcdaReceiptReasons.Referral,
    NewPatient: CcdaReceiptReasons.NewPatient,
    None: CcdaReceiptReasons.None,
};

// Fixed audit reason (FE string union) → the generated numeric enum sent on the wire. The names line up 1:1.
const UNMATCHED_REASON_TO_ENUM: Record<CcdaUnmatchedReason, CcdaUnmatchedReasons> = {
    NotOurPatient: CcdaUnmatchedReasons.NotOurPatient,
    AmbiguousCandidates: CcdaUnmatchedReasons.AmbiguousCandidates,
    DuplicateDocument: CcdaUnmatchedReasons.DuplicateDocument,
    Other: CcdaUnmatchedReasons.Other,
};

interface RawResolutionDocument {
    firstName?: string | null;
    middleName?: string | null;
    lastName?: string | null;
    dateOfBirth?: string | null;
    gender?: Genders | null;
    mrn?: string | null;
    addressLine?: string | null;
    city?: string | null;
    state?: string | null;
    postalCode?: string | null;
    phone?: string | null;
    fromDocumentBundle?: boolean;
}

interface RawMatchCriteria {
    firstName?: boolean;
    lastName?: boolean;
    dateOfBirth?: boolean;
    gender?: boolean;
    count?: number;
}

interface RawResolutionCandidate {
    patientId?: number;
    firstName?: string | null;
    middleName?: string | null;
    lastName?: string | null;
    dateOfBirth?: string | null;
    gender?: Genders | null;
    sexAtBirth?: SexAtBirths | null;
    hospitalMrn?: string | null;
    mbi?: string | null;
    hasActiveIntake?: boolean;
    facilityName?: string | null;
    isReceiveTimeSuggestion?: boolean;
    isSuggested?: boolean;
    matchedCriteria?: RawMatchCriteria | null;
}

interface RawPatientResolution {
    stagingId?: number;
    status?: string;
    receivedDate?: string | null;
    documentType?: string;
    senderName?: string | null;
    senderDirectAddress?: string | null;
    suggestedPatientId?: number | null;
    resolvedPatientId?: number | null;
    receiveTimeMatchesFound?: number;
    document?: RawResolutionDocument | null;
    candidates?: RawResolutionCandidate[] | null;
    matchTier?: string | null;
    canResolve?: boolean;
    rowVersion?: string | null;
}

// parseZone reads an offset-less value (the candidate DOB is a bare DateTime) as UTC, never local, so the
// UTC-formatted screen shows the same calendar day in every viewer timezone.
const wireIso = (value: string | null | undefined): string | null => (value ? iso(moment.parseZone(value)) : null);

const MATCH_TIERS: readonly CcdaMatchTier[] = ['FullMatch', 'PartialMatch', 'LowConfidence', 'Unmatched', 'NotEvaluated'];
// An absent or unrecognised tier claims nothing about the match.
const toMatchTier = (value: string | null | undefined): CcdaMatchTier => MATCH_TIERS.find((tier) => tier === value) ?? 'NotEvaluated';

// Absent criteria read as "none met", so the screen flags every criterion instead of implying agreement.
const mapMatchCriteria = (d: RawMatchCriteria | null | undefined): CcdaMatchCriteriaView => ({
    firstName: d?.firstName ?? false,
    lastName: d?.lastName ?? false,
    dateOfBirth: d?.dateOfBirth ?? false,
    gender: d?.gender ?? false,
    count: d?.count ?? 0,
});

const mapResolutionDocument = (d: RawResolutionDocument): CcdaResolutionDocumentView => ({
    firstName: d.firstName ?? null,
    middleName: d.middleName ?? null,
    lastName: d.lastName ?? null,
    dateOfBirth: wireIso(d.dateOfBirth),
    gender: genderLabel(d.gender),
    mrn: d.mrn ?? null,
    addressLine: d.addressLine ?? null,
    city: d.city ?? null,
    state: d.state ?? null,
    postalCode: d.postalCode ?? null,
    phone: d.phone ?? null,
    fromDocumentBundle: d.fromDocumentBundle ?? false,
});

const mapResolutionCandidate = (d: RawResolutionCandidate): CcdaResolutionCandidateView => ({
    patientId: d.patientId ?? 0,
    firstName: d.firstName ?? null,
    middleName: d.middleName ?? null,
    lastName: d.lastName ?? null,
    dateOfBirth: wireIso(d.dateOfBirth),
    gender: genderLabel(d.gender),
    sexAtBirth: sexAtBirthLabel(d.sexAtBirth),
    hospitalMrn: d.hospitalMrn ?? null,
    mbi: d.mbi ?? null,
    hasActiveIntake: d.hasActiveIntake ?? false,
    facilityName: d.facilityName ?? null,
    isReceiveTimeSuggestion: d.isReceiveTimeSuggestion ?? false,
    isSuggested: d.isSuggested ?? false,
    matchedCriteria: mapMatchCriteria(d.matchedCriteria),
});

const mapResolution = (d: RawPatientResolution): CcdaPatientResolutionView => ({
    stagingId: d.stagingId ?? 0,
    status: (d.status ?? 'PendingMatch') as CcdaReceiveStatus,
    receivedDate: wireIso(d.receivedDate),
    documentType: d.documentType ?? '',
    senderName: d.senderName ?? null,
    senderDirectAddress: d.senderDirectAddress ?? null,
    suggestedPatientId: d.suggestedPatientId ?? null,
    resolvedPatientId: d.resolvedPatientId ?? null,
    receiveTimeMatchesFound: d.receiveTimeMatchesFound ?? 0,
    document: d.document ? mapResolutionDocument(d.document) : null,
    candidates: (d.candidates ?? []).map(mapResolutionCandidate),
    matchTier: toMatchTier(d.matchTier),
    canResolve: d.canResolve ?? false,
    // Optimistic-concurrency stamp (v4) — echoed on mark-new-patient / mark-unmatched below; stale ⇒ 409.
    rowVersion: d.rowVersion ?? null,
});

// mark-new-patient / mark-unmatched return only { stagingId, status } (no bound patient). resolve-patient uses a
// separate result carrying resolvedPatientId, mapped inline below.
const mapOutcome = (d: CcdaResolutionOutcomeDto | null | undefined, stagingId: number, fallbackStatus: CcdaReceiveStatus): CcdaResolutionOutcome => ({
    stagingId: d?.stagingId ?? stagingId,
    status: (d?.status ?? fallbackStatus) as CcdaReceiveStatus,
    resolvedPatientId: null,
});

// AC-1: who the document says the patient is, plus the live chart candidates. Recomputed server-side on every call.
export const getPatientResolution = async (stagingId: number): Promise<CcdaPatientResolutionView> => {
    try {
        const response = await Axios.get<ApiEnvelope<RawPatientResolution>>(`${getEnvBaseUrl()}/api/ccda-receive/${stagingId}/patient-resolution`);
        checkEnvelope(response.data, 'Failed to load patient resolution.');
        if (response.data.entity == null) throw new Error('Failed to load patient resolution.');
        return mapResolution(response.data.entity);
    } catch (err) {
        throw toApiError(err, 'Failed to load patient resolution.');
    }
};

// AC-2 confirm / AC-4 handoff: bind the document to a chart → PendingReconciliation. 400 = no open intake; 409 = terminal/blocked.
export const resolvePatient = async (
    stagingId: number,
    patientId: number,
    receiptReason: CcdaReceiptReason,
    tin?: string,
): Promise<CcdaResolutionOutcome> => {
    try {
        const response = await client().resolvePatient(stagingId, new ResolvePatientRequest({ patientId, tin, receiptReason: RECEIPT_REASON_TO_ENUM[receiptReason] }));
        checkEnvelope(response.result, 'Failed to confirm the patient.');
        const entity = response.result.entity;
        return {
            stagingId: entity?.stagingId ?? stagingId,
            status: (entity?.status ?? 'PendingReconciliation') as CcdaReceiveStatus,
            resolvedPatientId: entity?.resolvedPatientId ?? patientId,
        };
    } catch (err) {
        throw toApiError(err, 'Failed to confirm the patient.');
    }
};

// AC-2 "new": park the row as "no chart yet" → PendingPatientCreation. Does NOT create a patient (AC-5). Echoes the
// payload's rowVersion for optimistic concurrency (v4) — a stale stamp ⇒ 409; null ⇒ last-write-wins.
export const markNewPatient = async (stagingId: number, rowVersion?: string | null): Promise<CcdaResolutionOutcome> => {
    try {
        const response = await client().markNewPatient(stagingId, new MarkNewPatientRequest({ rowVersion: rowVersion ?? null }));
        checkEnvelope(response.result, 'Failed to mark the patient as new.');
        return mapOutcome(response.result.entity, stagingId, 'PendingPatientCreation');
    } catch (err) {
        throw toApiError(err, 'Failed to mark the patient as new.');
    }
};

// AC-2 "unmatched": park the row as Rejected with a fixed audit reason (sent as the generated numeric enum). Echoes
// the payload's rowVersion for optimistic concurrency (v4) — a stale stamp ⇒ 409; null ⇒ last-write-wins. Reversible.
export const markUnmatched = async (stagingId: number, reason: CcdaUnmatchedReason, rowVersion?: string | null): Promise<CcdaResolutionOutcome> => {
    try {
        const response = await client().markUnmatched(stagingId, new MarkUnmatchedRequest({ reason: UNMATCHED_REASON_TO_ENUM[reason], rowVersion: rowVersion ?? null }));
        checkEnvelope(response.result, 'Failed to mark the document unmatched.');
        return mapOutcome(response.result.entity, stagingId, 'Rejected');
    } catch (err) {
        throw toApiError(err, 'Failed to mark the document unmatched.');
    }
};

// --- SCRUM-1171 admin ingest (§170.315(b)(1) receive) -----------------------------------------------------------
// The only write in this file whose body IS a clinical document. Three consequences, all deliberate:
//   * the envelope guard is inlined instead of reusing `checkEnvelope`, because that helper toasts through
//     handleError while the import modal renders the same failure in its own Alert — one failure, two reports.
//     fetchReceivedDocumentXml works around the same duplication.
//   * the telemetry exception carries a FIXED literal, never the server's message, so nothing document-derived
//     can reach App Insights.
//   * `fullException` is never surfaced — a raw server stack trace must not reach the UI.
// The document never enters Redux: the caller reads it, posts it, and drops it.
const INGEST_FAILED = 'Failed to import the C-CDA document.';

export const ingestCcda = async (ccdaXml: string, senderDirectAddress?: string | null, transport?: string | null): Promise<CcdaIngestResultView> => {
    try {
        const response = await client().ingest(
            new CcdaIngestRequest({
                ccdaXml,
                // Trimmed, not raw: a whitespace-only input would otherwise be persisted verbatim onto the staging
                // row and into the audit trail. Blank ⇒ omitted, matching Invoke-CcdaIngest.ps1.
                senderDirectAddress: senderDirectAddress?.trim() || null,
                transport: transport?.trim() || null,
            })
        );

        const env = response.result;
        if (!env.succeeded || env.entity == null) {
            appInsights.trackException({ exception: new Error(INGEST_FAILED) });
            throw new Error(env.message || INGEST_FAILED);
        }

        const entity = env.entity;

        return {
            stagingId: entity.stagingId ?? 0,
            status: (entity.status ?? 'PendingMatch') as CcdaReceiveStatus,
            ccdaVersion: entity.ccdaVersion ?? null,
            matchesFound: entity.matchesFound ?? 0,
            suggestedPatientId: entity.suggestedPatientId ?? null,
            failureReason: entity.failureReason ?? null,
            errorCount: entity.errorCount ?? 0,
        };
    } catch (err) {
        throw toApiError(err, INGEST_FAILED);
    }
};

// ---------------------------------------------------------------------------------------------------------
// SCRUM-927 — outbound Direct transmission.
//
// Hand-rolled against the shared Axios for the same reason as fetchInboxPage / fetchReceiveAuditPage above:
// @medone/medonehp-api-client resolves to the published registry package, so an endpoint that is not in it yet
// cannot be called through the generated client until a publish plus a pin bump. Revert to the generated
// client at the next publish.
//
// These live in api.ts rather than a sibling module on purpose. Every suite in this feature already
// `jest.mock('../api')`; a second api module would load for real under jsdom and drag MSAL in through the
// shared Axios, breaking seven unrelated suites at import time.
// ---------------------------------------------------------------------------------------------------------

const TRANSMIT_FAILED = 'The document could not be transmitted.';
const AUDIT_FAILED = 'Failed to load the transmission audit log.';
const RECIPIENT_REQUIRED = 'Enter the recipient’s Direct address.';

interface RawTransmitResult {
    ccdaDocumentId?: number;
    ccdaTransmissionId?: number;
    outcome?: string;
    transmissionStatus?: string;
    destination?: string | null;
    attemptedAt?: string | null;
    externalMessageId?: string | null;
    clientReference?: string | null;
    failureReason?: string | null;
    errors?: string[];
}

const mapTransmitResult = (d: RawTransmitResult): CcdaTransmitResultDto => ({
    ccdaDocumentId: d.ccdaDocumentId ?? 0,
    ccdaTransmissionId: d.ccdaTransmissionId ?? 0,
    // No default: a missing outcome must fall through every branch the UI knows, to its generic failure, rather
    // than be coerced into one of them. Defaulting to 'Sent' — or to 'Suppressed' — would be an assertion about
    // a transmission we have no word on.
    outcome: d.outcome ?? '',
    transmissionStatus: d.transmissionStatus ?? 'NotSent',
    destination: d.destination ?? null,
    attemptedAt: d.attemptedAt ?? null,
    externalMessageId: d.externalMessageId ?? null,
    clientReference: d.clientReference ?? null,
    failureReason: d.failureReason ?? null,
    errors: d.errors ?? [],
});

interface RawTransmissionAuditEntry {
    ccdaTransmissionId?: number;
    ccdaDocumentId?: number;
    patientId?: number | null;
    patientName?: string | null;
    facilityName?: string | null;
    providerName?: string | null;
    eventType?: string;
    timestamp?: string | null;
    userId?: string;
    userName?: string | null;
    documentType?: string | null;
    recipientDirectAddress?: string | null;
    transport?: string | null;
    externalMessageId?: string | null;
    transportReceiptPresent?: boolean;
    failureReason?: string | null;
    clientReference?: string | null;
}

const mapTransmissionAuditEntry = (d: RawTransmissionAuditEntry): CcdaTransmissionAuditEntryDto => ({
    ccdaTransmissionId: d.ccdaTransmissionId ?? 0,
    ccdaDocumentId: d.ccdaDocumentId ?? 0,
    patientId: d.patientId ?? null,
    patientName: d.patientName ?? null,
    facilityName: d.facilityName ?? null,
    providerName: d.providerName ?? null,
    eventType: d.eventType ?? '',
    timestamp: d.timestamp ?? null,
    userId: d.userId ?? '',
    userName: d.userName ?? null,
    documentType: d.documentType ?? null,
    recipientDirectAddress: d.recipientDirectAddress ?? null,
    transport: d.transport ?? null,
    externalMessageId: d.externalMessageId ?? null,
    // Defaults FALSE: an absent flag is "no receipt was evidenced", never a claim that one exists.
    transportReceiptPresent: d.transportReceiptPresent ?? false,
    failureReason: d.failureReason ?? null,
    clientReference: d.clientReference ?? null,
});

// One transmit attempt (SCRUM-927). A resolved promise is NOT a send: with no Direct adapter configured the
// server answers Suppressed / NotSent with a null destination, which is a successful, audited, inert attempt.
// Read `outcome` — never the absence of a throw.
// `smtpOverride` carries operator-typed SMTP credentials for THIS send only (b.1 ETT edge testing). They are
// put on the request body and dropped — never stored, never logged, and deliberately never passed to
// appInsights or into a thrown message: `toApiError` below builds every error from the RESPONSE, so no
// request field can ride out on a failure.
export const transmitCcda = async (ccdaDocumentId: number, recipientDirectAddress: string, smtpOverride?: CcdaTransmitSmtpOverride): Promise<CcdaTransmitResultDto> => {
    // Trimmed before the request, not after: this value is persisted verbatim onto the transmission row and
    // into the §170.315(d)(2)/(d)(3) audit trail, so " x@y " would become the recorded recipient forever. The
    // server trims too — this is the fast path that keeps a stray space out of the evidence, not the guarantee.
    const recipient = recipientDirectAddress.trim();

    // Thrown rather than posted: a blank recipient would mint an audit row for an attempt nobody could make.
    if (recipient.length === 0) {
        throw new Error(RECIPIENT_REQUIRED);
    }

    // Trimmed like the recipient — a stray space around a user name is a typo, not a credential.
    const smtpUserName = smtpOverride?.smtpUserName?.trim();
    // NOT trimmed, unlike every other field here: trailing whitespace can be part of a password, and
    // silently stripping it would surface as an authentication failure that looks like a wrong credential.
    const smtpPassword = smtpOverride?.smtpPassword;

    try {
        // Blank ⇒ the key is OMITTED, never sent as "". An empty string is a value the server would apply,
        // overriding its configured account with nothing.
        const body: Record<string, unknown> = { ccdaDocumentId, recipientDirectAddress: recipient };
        if (smtpUserName) body.smtpUserName = smtpUserName;
        if (smtpPassword) body.smtpPassword = smtpPassword;

        const response = await Axios.post<ApiEnvelope<RawTransmitResult>>(`${getEnvBaseUrl()}/api/ccda-documents/${ccdaDocumentId}/transmit`, body);

        // Guarded inline instead of through checkEnvelope: that helper toasts via handleError while the send
        // modal renders the same failure in its own Alert — one failure, two reports (the ingestCcda precedent
        // in api.ts). `fullException` is never surfaced either way.
        const env = response.data;
        if (!env.succeeded || env.entity == null) {
            throw new Error(env.message || TRANSMIT_FAILED);
        }

        return mapTransmitResult(env.entity);
    } catch (err) {
        throw toApiError(err, TRANSMIT_FAILED);
    }
};

// Per-patient outbound transmission audit (SCRUM-927 / §170.315(d)(2)/(d)(3)) — the outbound sibling of
// fetchReceiveAudit. Unpaged, like the inbound per-patient trail.
export const fetchPatientTransmissionAudit = async (patientId: number): Promise<CcdaTransmissionAuditEntryDto[]> => {
    try {
        const response = await Axios.get<ApiEnvelope<RawTransmissionAuditEntry[]>>(`${getEnvBaseUrl()}/api/ccda-documents/audit?patientId=${patientId}`);
        checkEnvelope(response.data, AUDIT_FAILED);

        return (response.data.entity ?? []).map(mapTransmissionAuditEntry);
    } catch (err) {
        throw toApiError(err, AUDIT_FAILED);
    }
};

export interface CcdaTransmissionAuditPageResult {
    items: CcdaTransmissionAuditEntryDto[];
    pagination: CcdaInboxPagination;
}

// Org-wide outbound transmission audit report (Admin ▸ System): admin-only, server-paged and filtered over the
// ASTM E2147 elements. The response is an UNWRAPPED AntdTableResponse — the ApiResponse body with a top-level
// pagination block — exactly like its inbound sibling fetchReceiveAuditPage.
export const fetchTransmissionAuditPage = async (filter: CcdaTransmissionAuditFilter, page: { current: number; pageSize: number }): Promise<CcdaTransmissionAuditPageResult> => {
    try {
        // Only keys the operator actually set travel. An ABSENT key is what the endpoint reads as "unfiltered";
        // an explicit null is a value it would have to special-case, and `patientId: null` in particular is one
        // model-binder coercion away from meaning patient 0.
        const body: Record<string, unknown> = { antdTableRequest: { current: page.current, pageSize: page.pageSize } };
        if (filter.fromDate) body.fromDate = filter.fromDate;
        if (filter.toDate) body.toDate = filter.toDate;
        if (filter.patientId != null) body.patientId = filter.patientId;
        if (filter.userId) body.userId = filter.userId;
        if (filter.eventType) body.eventType = filter.eventType;

        const response = await Axios.post<AntdEnvelope<RawTransmissionAuditEntry[]>>(`${getEnvBaseUrl()}/api/ccda-documents/audit/search`, body);
        checkEnvelope(response.data, AUDIT_FAILED);
        const pg = response.data.pagination;

        return {
            items: (response.data.entity ?? []).map(mapTransmissionAuditEntry),
            pagination: {
                current: pg?.currentPage ?? page.current,
                pageSize: pg?.pageSize ?? page.pageSize,
                total: pg?.total ?? 0,
            },
        };
    } catch (err) {
        throw toApiError(err, AUDIT_FAILED);
    }
};

// ---------------------------------------------------------------------------------------------------------
// SCRUM-922 — inbound Direct fetch over IMAP (§170.315(b)(1) receive).
//
// Hand-rolled against the shared Axios for the same reason as the SCRUM-927 block above, and living in this
// file for the same reason: a sibling api module would load for real under jsdom in every suite that only
// mocks '../api', dragging MSAL in through the shared Axios and breaking unrelated suites at import time.
// ---------------------------------------------------------------------------------------------------------

const FETCH_FAILED = 'Failed to read the Direct mailbox.';
const FETCH_RUNS_FAILED = 'Failed to load the inbound fetch history.';
const PACKAGE_FAILED = 'Failed to download the XDM package.';
const ATTACHMENT_FAILED = 'Failed to open the attachment.';

interface RawFetchRun {
    id?: number;
    status?: string;
    account?: string | null;
    mailboxName?: string | null;
    startedAt?: string | null;
    completedAt?: string | null;
    messagesInMailbox?: number;
    unreadInMailbox?: number;
    messagesProcessed?: number;
    documentsStaged?: number;
    errorCount?: number;
    failureReason?: string | null;
    startedBy?: string | null;
}

interface RawFetchMessage {
    id?: number;
    status?: string;
    transportMessageId?: string | null;
    senderDirectAddress?: string | null;
    sizeBytes?: number | null;
    attachmentCount?: number;
    documentsStaged?: number;
    duplicatesSkipped?: number;
    isPackage?: boolean;
    packageAvailable?: boolean;
    errorCount?: number;
    errors?: { code?: string | null; message?: string | null }[];
    attachments?: { index?: number; fileName?: string | null; contentType?: string; sizeBytes?: number }[];
}

const mapFetchRun = (d: RawFetchRun): CcdaFetchRunDto => ({
    id: d.id ?? 0,
    // No default: an absent status must fall through every branch the UI knows rather than be coerced into
    // one. Defaulting to 'Completed' would assert that a mailbox we have no word on was read successfully.
    status: d.status ?? '',
    account: d.account ?? null,
    mailboxName: d.mailboxName ?? null,
    startedAt: d.startedAt ?? null,
    completedAt: d.completedAt ?? null,
    messagesInMailbox: d.messagesInMailbox ?? 0,
    unreadInMailbox: d.unreadInMailbox ?? 0,
    messagesProcessed: d.messagesProcessed ?? 0,
    documentsStaged: d.documentsStaged ?? 0,
    errorCount: d.errorCount ?? 0,
    failureReason: d.failureReason ?? null,
    startedBy: d.startedBy ?? null,
});

const mapFetchMessage = (d: RawFetchMessage): CcdaFetchMessageDto => ({
    id: d.id ?? 0,
    status: d.status ?? '',
    transportMessageId: d.transportMessageId ?? null,
    senderDirectAddress: d.senderDirectAddress ?? null,
    sizeBytes: d.sizeBytes ?? null,
    attachmentCount: d.attachmentCount ?? 0,
    documentsStaged: d.documentsStaged ?? 0,
    duplicatesSkipped: d.duplicatesSkipped ?? 0,
    isPackage: d.isPackage ?? false,
    // Defaults FALSE: an absent flag is "no package was retained", never a claim that one is downloadable.
    packageAvailable: d.packageAvailable ?? false,
    errorCount: d.errorCount ?? 0,
    errors: (d.errors ?? []).map((e) => ({ code: e.code ?? null, message: e.message ?? null })),
    attachments: (d.attachments ?? []).map((a) => ({
        index: a.index ?? 0,
        fileName: a.fileName ?? null,
        contentType: a.contentType ?? 'application/octet-stream',
        sizeBytes: a.sizeBytes ?? 0,
    })),
});

// One mailbox read (SCRUM-922). A resolved promise is NOT a successful receive: a suppressed inbox or a
// missing mailbox answers 200 with status 'Failed' and a failureReason, which is a recorded, audited run that
// read nothing. Read `status` — never the absence of a throw.
//
// `overrides` carries an operator-typed mailbox account and folder list for THIS run only, because the ETT
// hosts a different mailbox per IMAP test. They go on the request body and are dropped — never stored, never
// logged, and deliberately never passed to appInsights or into a thrown message: `toApiError` builds every
// error from the RESPONSE, so no request field can ride out on a failure.
export const triggerCcdaFetch = async (overrides?: CcdaFetchOverrides): Promise<CcdaFetchRunDto> => {
    try {
        // Blank ⇒ the key is OMITTED, never sent as "". An empty string is a value the server would apply,
        // overriding its configured account with nothing.
        const body: Record<string, unknown> = {};
        // Trimmed like the recipient on the send side — a stray space around an account is a typo.
        const userName = overrides?.imapUserName?.trim();
        if (userName) body.imapUserName = userName;
        // NOT trimmed, unlike every other field here: trailing whitespace can be part of a password, and
        // silently stripping it would surface as an authentication failure that looks like a wrong credential.
        if (overrides?.imapPassword) body.imapPassword = overrides.imapPassword;
        const mailboxes = overrides?.mailboxNames?.trim();
        if (mailboxes) body.mailboxNames = mailboxes;

        const response = await Axios.post<ApiEnvelope<RawFetchRun>>(`${getEnvBaseUrl()}/api/ccda-receive/fetch`, body);

        // Guarded inline rather than through checkEnvelope: that helper toasts via handleError while the
        // screen renders the same failure in its own Alert — one failure, two reports (the ingestCcda
        // precedent above). `fullException` is never surfaced either way.
        const env = response.data;
        if (!env.succeeded || env.entity == null) {
            throw new Error(env.message || FETCH_FAILED);
        }

        return mapFetchRun(env.entity);
    } catch (err) {
        throw toApiError(err, FETCH_FAILED);
    }
};

export interface CcdaFetchRunPageResult {
    items: CcdaFetchRunDto[];
    pagination: CcdaInboxPagination;
}

// The inbound fetch history (Admin ▸ System): admin-only and server-paged. The response is an UNWRAPPED
// AntdTableResponse — the ApiResponse body with a top-level pagination block — like its two audit siblings.
export const fetchCcdaFetchRuns = async (filter: CcdaFetchRunFilter, page: { current: number; pageSize: number }): Promise<CcdaFetchRunPageResult> => {
    try {
        // Only keys the operator actually set travel; an ABSENT key is what the endpoint reads as unfiltered.
        const body: Record<string, unknown> = { antdTableRequest: { current: page.current, pageSize: page.pageSize } };
        if (filter.fromDate) body.fromDate = filter.fromDate;
        if (filter.toDate) body.toDate = filter.toDate;
        if (filter.status) body.status = filter.status;

        const response = await Axios.post<AntdEnvelope<RawFetchRun[]>>(`${getEnvBaseUrl()}/api/ccda-receive/fetch-runs/search`, body);
        checkEnvelope(response.data, FETCH_RUNS_FAILED);
        const pg = response.data.pagination;

        return {
            items: (response.data.entity ?? []).map(mapFetchRun),
            pagination: {
                current: pg?.currentPage ?? page.current,
                pageSize: pg?.pageSize ?? page.pageSize,
                total: pg?.total ?? 0,
            },
        };
    } catch (err) {
        throw toApiError(err, FETCH_RUNS_FAILED);
    }
};

// One run with its per-message outcomes — the AC4 evidence behind an expanded history row.
export const fetchCcdaFetchRunDetail = async (runId: number): Promise<CcdaFetchRunDetailDto> => {
    try {
        const response = await Axios.get<ApiEnvelope<{ run?: RawFetchRun; messages?: RawFetchMessage[] }>>(`${getEnvBaseUrl()}/api/ccda-receive/fetch-runs/${runId}`);
        checkEnvelope(response.data, FETCH_RUNS_FAILED);

        const entity = response.data.entity;
        return {
            run: mapFetchRun(entity?.run ?? {}),
            messages: (entity?.messages ?? []).map(mapFetchMessage),
        };
    } catch (err) {
        throw toApiError(err, FETCH_RUNS_FAILED);
    }
};

// ETT IMAP Test 28: one file kept beside a received C-CDA, read for the in-page viewer. Typed with the content type the
// server stored, so a PDF renders as a PDF whatever the sender labelled it.
export const fetchCcdaFetchAttachment = async (runId: number, messageId: number, index: number, contentType: string): Promise<Blob> => {
    try {
        const response = await Axios.get<Blob>(`${getEnvBaseUrl()}/api/ccda-receive/fetch-runs/${runId}/messages/${messageId}/attachments/${index}`, {
            responseType: 'blob',
        });
        return new Blob([response.data], { type: contentType });
    } catch (err) {
        throw toApiError(err, ATTACHMENT_FAILED);
    }
};

// Streams the retained XDM package and forces a browser download (SCRUM-922 AC3). Addressed by run + message
// id, never by blob key — the server resolves the key, so this cannot ask for an arbitrary object in the
// inbound container. The bytes are PHI: held in memory only, never logged, and the object URL is revoked.
export const downloadCcdaFetchPackage = async (runId: number, messageId: number): Promise<void> => {
    try {
        const response = await Axios.get<Blob>(`${getEnvBaseUrl()}/api/ccda-receive/fetch-runs/${runId}/messages/${messageId}/package`, { responseType: 'blob' });

        const blob = new Blob([response.data], { type: 'application/zip' });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.setAttribute('download', `xdm-package-${runId}-${messageId}.zip`);
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        URL.revokeObjectURL(url);
    } catch (err) {
        throw toApiError(err, PACKAGE_FAILED);
    }
};
